import type { InvoicingStatus, TmsInvoiceRow, TmsWorklistPeriod } from '@numeo/types-main-web-app'
import { formatCount, formatDayRange, formatUsd } from '../results/format'
import { amountOf, statusOf } from './invoicingRows'

interface DeliveryGroup {
    label: string
    count: number
    amount: number
}

/**
 * v17's four groups, every status in exactly one, so the rows add up to the Delivered total. A row blocked by something other than its
 * POD (`other`: a rate or a rate con missing) still needs documents first, as the quick answer's copy counts it; an emailed invoice is
 * past its submission, so it counts as prepared (the table's Prepared filter lists both).
 */
const GROUPS: Array<{ label: string; statuses: InvoicingStatus[] }> = [
    { label: 'Ready for submission', statuses: ['ready', 'draft'] },
    { label: 'Missing documents', statuses: ['missing_pod', 'other'] },
    { label: 'POD issues', statuses: ['needs_review'] },
    { label: 'Submission prepared', statuses: ['batch_prepared', 'sent'] },
]

/** The delivered rows grouped by invoice readiness. The footer's total is the sum of these same groups, so the copy's count matches. */
export function deliveryStatsOf(rows: TmsInvoiceRow[]): { groups: DeliveryGroup[]; count: number; amount: number } {
    const groups = GROUPS.map(({ label, statuses }) => {
        const inGroup = rows.filter((row) => statuses.includes(statusOf(row)))
        return { label, count: inGroup.length, amount: inGroup.reduce((sum, row) => sum + amountOf(row), 0) }
    })
    return {
        groups,
        count: rows.length,
        amount: rows.reduce((sum, row) => sum + amountOf(row), 0),
    }
}

interface DeliveryStatsTableProps {
    rows: TmsInvoiceRow[]
    /** The delivery window the read answered for, when it names one: shown as the period line under the answer's sentence. */
    period?: TmsWorklistPeriod
}

/**
 * The Submit suggestion's answer: the delivered loads by readiness, one line each, with the delivered total at the foot. The sentence
 * that introduces it is the answer's own reply (above the table), so the table adds only the period it covers.
 */
export const DeliveryStatsTable = ({ rows, period }: DeliveryStatsTableProps) => {
    const stats = deliveryStatsOf(rows)
    return (
        <div className="flex flex-col gap-2">
            {period && <small className="text-xs text-foreground-muted">{formatDayRange(period.from, period.to)}</small>}
            <div className="overflow-x-auto rounded-xl border border-border">
                <table aria-label="Delivered load statistics" className="w-full text-xs">
                    <thead className="bg-surface-secondary text-foreground-muted">
                        <tr>
                            <th className="px-3 py-2 text-left font-medium">Status</th>
                            <th className="px-3 py-2 text-right font-medium">Loads</th>
                            <th className="px-3 py-2 text-right font-medium">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {stats.groups.map((group) => (
                            <tr key={group.label} className="border-t border-border">
                                <td className="px-3 py-2">{group.label}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{formatCount(group.count)}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{formatUsd(group.amount)}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot className="bg-surface-secondary font-semibold">
                        <tr className="border-t border-border">
                            <td className="px-3 py-2">Delivered</td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatCount(stats.count)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatUsd(stats.amount)}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    )
}
